/**
 * 画面単位のエラー境界。
 *
 * React は描画中に例外が出ると、境界が無い場合ツリー全体を unmount する。
 * これまで境界が 1 つも無かったため、1 画面の不具合でサイドバーごと真っ白になり、
 * 見ている人が他の画面へ戻る手段を失っていた（2026-08-09 実測: ファネル画面）。
 *
 * ここでは「落ちた画面だけ」を差し替え、ナビゲーションは残す。
 */
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertCircle } from 'lucide-react'

interface Props {
  children: ReactNode
  /** route が変わったら境界をリセットするためのキー（通常は pathname）。 */
  resetKey?: string
}

interface State {
  error: Error | null
}

export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidUpdate(prevProps: Props): void {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error !== null) {
      this.setState({ error: null })
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // 握りつぶさない。原因追跡できるよう component stack ごと残す。
    console.error('[RouteErrorBoundary] 画面の描画に失敗しました', error, info.componentStack)
  }

  render(): ReactNode {
    const { error } = this.state
    if (error === null) return this.props.children

    return (
      <div className="p-6">
        <div className="mx-auto max-w-xl rounded-lg border border-destructive/30 bg-destructive/5 p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" aria-hidden />
            <div className="min-w-0">
              <h2 className="font-semibold">この画面を表示できませんでした</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                他の画面は左のメニューからそのまま使えます。
                何度も出る場合は、下の内容を添えて連絡してください。
              </p>
              <pre className="mt-3 overflow-x-auto rounded bg-muted p-3 text-xs text-muted-foreground">
                {error.message}
              </pre>
            </div>
          </div>
        </div>
      </div>
    )
  }
}
